import type { ReactNode } from 'react';
import { Confirm, type AdminCtx } from '../ui/Shell';
import { Block, type Act, type BlockProps, type Tone } from '../ui/Block';
import { ApiErr, post, type Env } from '../lib/api';

export const LANG: Record<string, string> = { en: 'English', de: 'German', ro: 'Romanian', sv: 'Swedish', es: 'Spanish', fr: 'French', ja: 'Japanese', pt: 'Portuguese', it: 'Italian', nl: 'Dutch', pl: 'Polish', uk: 'Ukrainian', kk: 'Kazakh', hr: 'Croatian', lt: 'Lithuanian', ru: 'Russian', tr: 'Turkish', zh: 'Chinese', ko: 'Korean', ar: 'Arabic', hi: 'Hindi' };
export const langName = (l?: string | null) => (l ? LANG[l] ?? l : 'not set');

export const SRC: Record<string, string> = { mic: 'Microphone', youtube: 'YouTube live', radio: 'Radio stream' };
export const srcName = (s?: string | null) => (s ? SRC[s] ?? s : '—');
export const ORIGIN: Record<string, string> = { dashboard: 'Dashboard', api: 'API', batch_api: 'Batch API', mcp: 'MCP', extension: 'Extension', desktop_app: 'Desktop App', obs: 'OBS', wordpress: 'WordPress', payload: 'Payload', strapi: 'Strapi', sanity: 'Sanity', contentful: 'Contentful', broadcast: 'Broadcast', unknown: 'Unknown' };
export const originName = (o?: string | null) => (o ? ORIGIN[o] ?? o : '—');

export const kindLabel = (t: { live: boolean; jobSource?: string | null }) => (t.live ? 'LIVE' : t.jobSource === 'url' ? 'LINK' : 'FILE');

export const OPEN = ['pending', 'live', 'paused', 'processing'];
export const isOpen = (s?: string | null) => !!s && OPEN.includes(s);

export const RST: Record<string, Tone> = { pending: 'mute', live: 'live', paused: 'warn', processing: 'info', ended: 'mute', finalized: 'ok' };
export const recPill = (s?: string | null): { label: string; tone: Tone } => ({ label: (s ?? '—').toUpperCase(), tone: RST[s ?? ''] ?? 'mute' });

export const JST: Record<string, Tone> = { failed: 'bad', running: 'info', queued: 'mute', done: 'ok', stopped: 'mute', succeeded: 'ok' };
export const jobPill = (s?: string | null, willNotRetry?: boolean): { label: string; tone: Tone } => (willNotRetry ? { label: 'FAILED', tone: 'bad' } : { label: (s ?? '—').toUpperCase(), tone: JST[s ?? ''] ?? 'mute' });

export const finPill = (fin?: string | null, txFailed?: boolean): { label: string; tone: Tone } => (fin === 'failed' || txFailed ? { label: 'FAILED', tone: 'bad' } : fin === 'queued' ? { label: 'QUEUED', tone: 'mute' } : fin === 'running' ? { label: 'RUNNING', tone: 'info' } : fin === 'done' ? { label: 'DONE', tone: 'ok' } : { label: 'NONE', tone: 'mute' });

export const ART: Record<string, Tone> = { uploading: 'info', sealed: 'ok', failed: 'bad', deleted: 'mute' };

export const shareHost = (env: Env) => (env.key === 'production' ? 'https://live.vocalstack.com' : 'https://app.e2e.vocalstack.com');

export const andList = (a: string[]) => (a.length < 2 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);
export const plural = (n: number, w: string) => `${n.toLocaleString()} ${w}${n === 1 ? '' : 's'}`;
export const shortId = (id?: string | null) => (id ? `${id.slice(0, 8)}…` : '—');
export const errText = (e: unknown) => (e instanceof ApiErr ? e.message : (e as Error)?.message ?? 'failed');

export interface DAct extends Act { desk?: boolean; href?: string }

export const forDevice = (mobile: boolean, acts: (DAct | null | false)[]): DAct[] => acts.filter((a): a is DAct => !!a).filter((a) => !(mobile && a.desk));

export function DrawerBody({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>;
}

export function DBlock(p: BlockProps) {
  return <Block {...p} style={{ height: 'auto', ...p.style }} />;
}

export function DrawerActs({ acts }: { acts: DAct[] }) {
  if (!acts.length) return null;
  return (
    <>
      {acts.map((a) => a.href ? (
        <a key={a.label} className={`btn btn--sm ${a.danger ? 'btn--danger' : ''} ${a.primary ? 'btn--primary' : ''}`} href={a.href} target="_blank" rel="noreferrer" title={a.title}>{a.label}</a>
      ) : (
        <button key={a.label} type="button" className={`btn btn--sm ${a.danger ? 'btn--danger' : ''} ${a.primary ? 'btn--primary' : ''}`} onClick={a.onClick} title={a.title} disabled={a.disabled}>{a.label}</button>
      ))}
    </>
  );
}

export function confirmSimple(ctx: AdminCtx, o: { title: ReactNode; body?: ReactNode; app?: string; confirmLabel: string; danger?: boolean; list?: [string, string][]; onConfirm: () => Promise<void> }) {
  ctx.openModal(<Confirm env={ctx.env} mobile={ctx.mobile} app={o.app} title={o.title} body={o.body} list={o.list} danger={o.danger} confirmLabel={o.confirmLabel} onConfirm={o.onConfirm} onClose={ctx.closeModal} />);
}

export interface ParentRow { id: string; name: string; lifecycle: string }

export function lifecycleActs(ctx: AdminCtx, kind: 'broadcast' | 'transcription', s: ParentRow, label: string, opts: { busy?: string | null; after?: () => void; onPurged?: () => void }): DAct[] {
  const env = ctx.env;
  const after = () => opts.after?.();
  const flip = async (to: 'archive' | 'restore') => {
    try {
      await post(env, `/admin/${kind}s/${s.id}/${to}`);
    } catch (e) {
      ctx.toast(`${to === 'archive' ? 'Archive' : 'Restore'} failed`, errText(e), { logged: false });
      return;
    }
    after();
    ctx.toast(`${to === 'archive' ? 'Archived' : 'Restored'} ${label}`, '', { undo: () => { void flip(to === 'archive' ? 'restore' : 'archive'); } });
  };
  if (s.lifecycle === 'archived') {
    return [
      { label: 'Restore', onClick: () => confirmSimple(ctx, { title: `Restore ${kind}`, app: label, body: `${label} comes back from the archive.`, confirmLabel: 'Restore', onConfirm: () => flip('restore') }) },
      { label: 'Purge', danger: true, desk: true, title: 'Deletes the rows and their storage objects; requires the typed id', onClick: () => ctx.openModal(
        <Confirm env={env} mobile={ctx.mobile} deskOnly app={kind === 'broadcast' ? `${shareHost(env).replace('https://', '')}/${label}` : label} title={`Purge ${kind}`} typed={s.id} danger confirmLabel="Purge"
          body={`Purging deletes the rows and every storage object they own. ${kind === 'broadcast' ? 'The code is tombstoned and can never be reused.' : 'Share links stop working.'} This cannot be undone.`}
          onConfirm={async () => { await post(env, `/admin/${kind}s/${s.id}/purge`, { confirm: s.id }); ctx.toast(`Purged ${label}`, kind === 'broadcast' ? 'Code tombstoned' : 'Rows and storage objects deleted'); opts.onPurged?.(); after(); }} onClose={ctx.closeModal} />,
      ) },
    ];
  }
  return [{ label: 'Archive', title: opts.busy ? `Refused while ${opts.busy}` : undefined, onClick: () => {
    if (opts.busy) {
      ctx.openModal(<Confirm env={env} mobile={ctx.mobile} app={kind} title="Archive refused" body={`${label} is ${opts.busy}. ${kind === 'broadcast' ? 'A broadcast on air cannot be archived; stop the recording first.' : 'End or force stop the recording first.'}`} cancelLabel="Close" onClose={ctx.closeModal} />);
      return;
    }
    confirmSimple(ctx, { title: `Archive ${kind}`, app: label, danger: true, body: `${label} moves to the archive; Restore brings it back.`, confirmLabel: 'Archive', onConfirm: () => flip('archive') });
  } }];
}
